import { useMsal } from "@azure/msal-react";
import PropTypes from "prop-types";
import React, { useContext, useState, useCallback, useMemo } from "react";
import { ShotClockService } from "../../services/ShotClockService";
import { ActiveUserContext, AlertContext } from "../Dashboard/Dashboard";
import BarShotClock from "./BarShotClock/BarShotClock";
import TableShotClock from "./TableShotClock/TableShotClock";

const ShotClock = React.memo(({
  load,
  type,
  setAvailableToBook,
  active,
  actionCallback
}) => {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const activeUser = useContext(ActiveUserContext);
  const [isLoading, setIsLoading] = useState(false);

  // Memoize orderId calculation
  const orderId = useMemo(() => load.orderId ?? load.id, [load.orderId, load.id]);

  // Memoize movementId reference
  const movementId = useMemo(() => load.movementId, [load.movementId]);

  // Memoize handleResponse callback
  const handleResponse = useCallback(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 3000);
  }, []); // No dependencies - function never changes

  // Memoize handleLockLoad callback
  const handleLockLoad = useCallback(() => {
    setIsLoading(true);
    ShotClockService.LockLoad(instance, movementId, orderId)
      .then(() => {
        handleResponse();
      })
      .catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
  }, [instance, movementId, orderId, handleResponse, warning]);

  // Memoize the component selection
  const ShotClockComponent = useMemo(() => {
    return type === "table" ? TableShotClock : BarShotClock;
  }, [type]);

  // Memoize common props
  const commonProps = useMemo(() => ({
    activeUser,
    isLoading,
    load,
    handleLockLoad,
    setAvailableToBook,
    actionCallback
  }), [activeUser, isLoading, load, handleLockLoad, setAvailableToBook, actionCallback]);

  return (
    <ShotClockComponent
      {...commonProps}
      {...(type === "table" && { active })}
    />
  );
}, (prevProps, nextProps) => {
  // Custom comparison - return true if props are equal (skip re-render)
  return (
    (prevProps.load.orderId ?? prevProps.load.id) === (nextProps.load.orderId ?? nextProps.load.id) &&
    prevProps.load.movementId === nextProps.load.movementId &&
    prevProps.type === nextProps.type &&
    prevProps.active === nextProps.active &&
    prevProps.actionCallback === nextProps.actionCallback &&
    prevProps.setAvailableToBook === nextProps.setAvailableToBook
  );
});

ShotClock.displayName = 'ShotClock';

ShotClock.propTypes = {
  load: PropTypes.object,
  type: PropTypes.string,
  setAvailableToBook: PropTypes.func.isRequired,
  active: PropTypes.bool,
  actionCallback: PropTypes.func.isRequired
};

export default ShotClock;